import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Shared bits used by every group screen — extracted into their own file
 * to avoid circular imports (HolderGroupPage <-> HolderGroupMembers etc.
 * all need these, and importing them from HolderGroupPage created a
 * webpack TDZ at runtime).
 */

// Per-group holder rank context — provider lives in HolderGroupPage,
// consumer is AuthorBlock below.
export const HolderRanksContext = React.createContext({ ranks: {}, total: 0 });

// Branded loading indicator used across group, thread, members, index.
export function SlurpLoader({ label = "Loading" }) {
  return (
    <div className="pm-slurp-loader" role="status" aria-live="polite">
      <img src="/images/png/slurpface (1).png" alt="Slurp" style={{ width: 64, height: 64 }} />
      <span className="pm-slurp-loader__label">{label}</span>
    </div>
  );
}

const truncWallet = (w) => (w && w.length >= 4 ? w.slice(-4) : w || "");

// Avatar + display name + role badge + holder-rank badge. Wrapped in
// an inline element that navigates to the user's profile on click and
// stops propagation so a click on the author inside a thread card
// doesn't also fire the parent <Link>'s navigation.
export function AuthorBlock({ wallet, username, imageUrl, role, onDark = false }) {
  const navigate = useNavigate();
  const { ranks, total } = React.useContext(HolderRanksContext);
  const display = username ? `@${username}` : truncWallet(wallet);
  const initials = (username || wallet || "?").slice(0, 2).toUpperCase();
  const goProfile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (wallet) navigate(`/profile/${wallet}`);
  };
  // Holder badge tiers — TOP 10 / TOP 100 / TOP 1% (1% only kicks in
  // when the holder set is large enough to be more selective than 100).
  const rank = ranks?.[wallet];
  const holderBadge = (() => {
    if (!rank) return null;
    if (rank <= 10) return { label: "TOP 10", className: "pm-group__role--holder10" };
    if (rank <= 100) return { label: "TOP 100", className: "pm-group__role--holder100" };
    if (total >= 1000 && rank <= Math.ceil(total * 0.01)) {
      return { label: "TOP 1%", className: "pm-group__role--holder1pct" };
    }
    return null;
  })();
  return (
    <span
      className={`pm-group__author${onDark ? " is-dark" : ""}`}
      onClick={goProfile}
      role="link"
      tabIndex={0}
      title={username ? `@${username}` : wallet}
    >
      <span className="pm-group__author-avatar" aria-hidden="true">
        {imageUrl ? <img src={imageUrl} alt="" /> : <span className="pm-group__author-avatar-fallback">{initials}</span>}
      </span>
      <span className="pm-group__author-name">{display}</span>
      {role === "creator" && <span className="pm-group__role pm-group__role--creator">CREATOR</span>}
      {role === "admin"   && <span className="pm-group__role pm-group__role--admin">ADMIN</span>}
      {holderBadge && <span className={`pm-group__role ${holderBadge.className}`}>{holderBadge.label}</span>}
    </span>
  );
}
